import { layoutProcess } from "./bpmnAutoLayout";
import type { BPMNProcess, BPMNElement } from "../types";
import { logger } from '../../../../../../../constant/winston'

type KeyAndTopicMap = {
  [key: string]: string
}

export async function getXMLFromJSON(name: string, json: BPMNProcess, keyAndTopicMap: KeyAndTopicMap) {
  const processTags = Object.entries(json).map(([key, val]) => {
    return getTag(key, val, keyAndTopicMap);
  });
  const defs = getBPMNDefinitions(name, processTags.join("\n"));
  logger.info("getBPMNDefinitions: ", defs)
  try {
    const data = await layoutProcess(defs);
    logger.info("layoutProcess: ", data)
    const subprocessIDs = Object.entries(json)
      .filter(([_, val]) => val.type === "subProcess")
      .map(([key]) => key);
    const subprocessDiagramNodes = extractSubprocessDiagram(
      data,
      subprocessIDs
    );
    const xml = appendSubprocessDiagramNodes(
      data,
      subprocessDiagramNodes,
    );
    logger.info("appendSubprocessDiagramNodes:", xml)
    return xml;
  } catch (err) {
    logger.error("error: getXMLFromJSON:", err)
    throw err;
  }
}

function getTag(key: string, element: BPMNElement, keyAndTopicMap: KeyAndTopicMap): string {
  const topicKey = keyAndTopicMap[key]
  const { type } = element;

  if (element.type === "sequenceFlow") {
    const { sourceRef, targetRef } = element;
    return `<bpmn:sequenceFlow id="${key}" sourceRef="${sourceRef}" targetRef="${targetRef}"/>`;
  }

  let name = "";
  let incoming = "incoming" in element ? element.incoming : undefined;
  let outgoing = "outgoing" in element ? element.outgoing : undefined

  if (element.type !== "subProcess") {
    name = element.name;
  }

  let openTag = `<bpmn:${type} id="${key}" ${!name ? "" : `name="${name}"`} `
  if (topicKey && (element.type === "userTask" || (element.type === "serviceTask" && !element.externalTask))) {
    openTag += `camunda:${element.type === "userTask" ? "formKey" : "topic"}="a8studio:${topicKey}" `
  }

  // NOTE: only handling uniphore ai agents / actions for now
  if (element.type === 'serviceTask' && element.externalTask === "aiAgent") {
    openTag += `camunda:type="external" camunda:topic="aiAgentTask" camunda:aiAgentKey="uniphore:${element.name}"`
  }
  if (element.type === 'serviceTask' && element.externalTask === "action") {
    openTag += `camunda:type="external" camunda:topic="actionTask" camunda:actionKey="uniphore:${element.integrationName}:${element.name}"`
  }

  openTag += ">"
  const closeTag = `</bpmn:${type}>`;

  const body: string[] = [];
  body.push(...getConnection(outgoing, "outgoing"));
  body.push(...getConnection(incoming, "incoming"));

  if (element.type === "intermediateCatchEvent") {
    const { timerEventDefinition: t, messageEventDefinition: m } = element
    if (t) {
      body.push(`<bpmn:timerEventDefinition id="${t}_def" />`)
    }
    if (m) {
      body.push(`<bpmn:messageEventDefinition id="${m}_def" />`)
    }
  }

  if (element.type !== "subProcess") {
    return [openTag, ...body, closeTag].join("\n");
  }

  const processTags = Object.entries(element.process).map(([key, val]) => {
    return getTag(key, val, keyAndTopicMap)
  });

  return [openTag, ...body, ...processTags, closeTag].join("\n");
}


function appendSubprocessDiagramNodes(
  xml: string,
  nodes: Element[],
) {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xml, "text/xml");
  const diagrams = Array.from(
    xmlDoc.getElementsByTagName("bpmndi:BPMNPlane")
  ).filter((el) => el.id === "BPMNPlane_tars_bpmngenerator");

  if (diagrams.length) {
    nodes.forEach((node) => {
      diagrams[0].appendChild(node);
    });
  }

  // delete all other diagrams
  Array.from(xmlDoc.getElementsByTagName("bpmndi:BPMNDiagram"))
    .filter((el) => el.id !== "BPMNDiagram_tars_bpmngenerator")
    .forEach((el) => {
      el.remove();
    });
  return xmlDoc.documentElement.outerHTML;
}

function extractSubprocessDiagram(xml: string, subprocessIDs: string[]) {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xml, "text/xml");

  const diagrams = Array.from(
    xmlDoc.getElementsByTagName("bpmndi:BPMNDiagram")
  ).filter((el) => subprocessIDs.includes(el.id.replace("BPMNDiagram_", "")));

  let result: Element[] = [];
  diagrams.forEach((d) => {
    const edges = Array.from(d.getElementsByTagName("bpmndi:BPMNEdge"));
    const shapes = Array.from(d.getElementsByTagName("bpmndi:BPMNShape"));
    result = [...edges, ...shapes];
  });

  return result;
}

function getBPMNDefinitions(name: string, process: string) {
  return `
    <bpmn:definitions 
      xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" 
      xmlns:bpmn="http://www.omg.org/spec/BPMN/20100524/MODEL" 
      xmlns:bpmndi="http://www.omg.org/spec/BPMN/20100524/DI" 
      xmlns:dc="http://www.omg.org/spec/DD/20100524/DC" 
      xmlns:camunda="http://camunda.org/schema/1.0/bpmn" 
      xmlns:di="http://www.omg.org/spec/DD/20100524/DI" 
      targetNamespace="http://bpmn.io/schema/bpmn"
    >
      <bpmn:process id="${name}_bpmngenerator" name="bpmngenerator" isExecutable="true" camunda:historyTimeToLive="P30D">
         ${process}
      </bpmn:process>
    </bpmn:definitions>
`;
}

function getConnection(
  connections: string[] | string | undefined,
  type: string
) {
  const target = Array.isArray(connections)
    ? connections
    : connections === undefined
      ? []
      : [connections];
  return target.map(
    (connection) => `<bpmn:${type}>${connection}</bpmn:${type}>`
  );
}
